import * as React from 'react';
import './EvaluateComponent.css';

import Timeout = NodeJS.Timeout;
import {clearTimeout, setTimeout} from "timers";

import {t} from 'react-multi-lang';
import {IEvaluateProps} from "../../containers/EvaluateContainer";
import IShortlistable from "../../interfaces/IShortlistable";
import IShortlistElement from "../../interfaces/IShortlistElement";
import ISource from "../../interfaces/ISource";
import IUsability, {usabilityToString} from "../../interfaces/IUsability";

interface IEvaluateState {
    editing: boolean,
    source: ISource | undefined,
}

class EvaluateComponent extends React.Component<IEvaluateProps, IEvaluateState> {
    public static getDerivedStateFromProps(props: IEvaluateProps, state: IEvaluateState) {
        const id = EvaluateComponent.idFromPath(props);

        return {
            ...state,
            source: props.sources.find((s: ISource) => s._id === id)
        }
    }

    private static idFromPath(props: IEvaluateProps) {
        const search = props.history.location.search.split('?');

        for (const searchString of search) {
            const [key, value] = searchString.split('=');
            if (key === 'id') {
                return value;
            }
        }

        const firstUnrated = props.sources.find((s: ISource) => s.usability === IUsability.Unrated);
        return firstUnrated !== undefined ? firstUnrated._id : '-1';
    }

    private commentDispatcher: Timeout;

    constructor(props: IEvaluateProps) {
        super(props);

        this.state = {
            editing: false,
            source: undefined,
        };

        this.modeSwitch = this.modeSwitch.bind(this);
        this.editingBadge = this.editingBadge.bind(this);
        this.titleToDiv = this.titleToDiv.bind(this);
        this.yearToDiv = this.yearToDiv.bind(this);
        this.authorsToDiv = this.authorsToDiv.bind(this);
        this.keywordsToDiv = this.keywordsToDiv.bind(this);
        this.abstractToDiv = this.abstractToDiv.bind(this);

        this.handleClassification = this.handleClassification.bind(this);
        this.handleQuote = this.handleQuote.bind(this);

        this.sourceFromID = this.sourceFromID.bind(this);
        this.isShortlisted = this.isShortlisted.bind(this);

        this.handleTitleChange = this.handleTitleChange.bind(this);
        this.handleYearChange = this.handleYearChange.bind(this);
        this.handleAuthorChange = this.handleAuthorChange.bind(this);
        this.handleKeywordChange = this.handleKeywordChange.bind(this);
        this.handleAbstractChange = this.handleAbstractChange.bind(this);
        this.handleCommentChange = this.handleCommentChange.bind(this);
    }

    public render() {
        if (this.state.source === undefined) {
            return (
                <>
                    <div className="w_header">{t('evaluate.evaluate')}</div>
                    <div className="w_component w_component_fill text-center">
                        <h6>{t('evaluate.no-sources')}</h6>
                    </div>
                </>
            );
        }

        const remaining = this.props.sources.filter(s => s.usability === IUsability.Unrated).length;

        return (
            <>
                <div className="w_header">
                    <span>{t('evaluate.evaluate')}</span>
                    {this.editingBadge()}
                    {remaining > 0 &&
                    <span className="ml-auto">{remaining} {t('evaluate.remaining')}</span>}
                </div>
                {!this.state.editing ?
                    <div className="w_component w_component_fill">{t('evaluate.description_evaluate')}</div> :
                    <div className="w_component w_component_fill">{t('evaluate.description_edit')}</div>}
                <div className="w_component w-100">
                    <label htmlFor="eval_title">{t('evaluate.title')}:</label>
                    {this.titleToDiv(this.state.source)}
                    <label htmlFor="eval_year">{t('evaluate.year')}:</label>
                    {this.yearToDiv(this.state.source)}
                    <label htmlFor="eval_authors">{t('evaluate.authors')}:</label>
                    <div id="eval_authors" className="w_component_row">
                        {this.authorsToDiv(this.state.source)}
                    </div>
                    {((this.state.source.keywords.length > 0 && this.state.source.keywords[0]) || this.state.editing) &&
                    <>
                        <label htmlFor="eval_keywords">{t('evaluate.keywords')}:</label>
                        <div id="eval_keywords" className="w_component_row">
                            {this.keywordsToDiv(this.state.source)}
                        </div>
                    </>
                    }
                    {(this.state.source.abstract || this.state.editing) &&
                    <>
                        <label htmlFor="eval_abstract">{t('evaluate.abstract')}:</label>
                        <div className="w_component_row" id="eval_abstract">
                            {this.abstractToDiv(this.state.source)}
                            {!this.state.editing &&
                            <div>
                                <button onMouseDown={this.handleQuote} className="btn btn-outline-info mt-1"
                                        title={t('evaluate.shortlist_hover')}>
                                    {t('evaluate.shortlist')}
                                </button>
                            </div>}
                        </div>
                    </>}
                    {!this.state.editing &&
                    <>
                        <label htmlFor="eval_comment">{t('evaluate.comment')}:</label>
                        <textarea className="w_component_row form-control" id="eval_comment" name="comment" rows={5}
                                  value={this.state.source.comment || ""} onChange={this.handleCommentChange}/>
                        <div className="w_component_row mt-3">
                            {t('evaluate.contribution')}: {usabilityToString(this.state.source.usability)}
                        </div>
                        <div className="w_component_row mt-3">
                            <button value={IUsability.Rejected} onClick={this.handleClassification}
                                    className="btn btn-danger mr-1"
                                    title={t('evaluate.reject_hover')}>{t('evaluate.reject')}
                            </button>
                            <button value={IUsability.Tentative} onClick={this.handleClassification}
                                    className="btn btn-info mr-1"
                                    title={t('evaluate.tentative_hover')}>{t('evaluate.tentative')}
                            </button>
                            <button value={IUsability.Accepted} onClick={this.handleClassification}
                                    className="btn btn-success mr-1"
                                    title={t('evaluate.accept_hover')}>{t('evaluate.accept')}
                            </button>
                        </div>
                    </>}
                </div>
            </>
        );
    }

    private editingBadge() {
        if (this.state.editing) {
            return (
                <button className='btn btn-sm btn-secondary btn-success ml-3' onClick={this.modeSwitch}
                        title={t('evaluate.save_hover')}>
                    {t('evaluate.save')}
                </button>
            );
        } else {
            return (
                <button className='btn btn-sm btn-outline-info ml-3' onClick={this.modeSwitch}
                        title={t('evaluate.edit_hover')}>
                    {t('evaluate.edit')}
                </button>
            );
        }
    }

    private titleToDiv(source: ISource) {
        if (this.state.editing) {
            return (
                <div id="eval_title" className="w_component_row">
                    <input className="form-control" type="text" id="edit_title" name="title"
                           required={true} value={source.title} onChange={this.handleTitleChange}/>
                </div>
            );
        }

        let badgeColor;
        let onClickFunction;

        if (this.isShortlisted(IShortlistable.Title, source.title.trim())) {
            badgeColor = "badge-success";
            onClickFunction = () => this.props.dispatchShortlistRemove(IShortlistable.Title, source.title.trim());
        } else {
            badgeColor = "badge-info";
            onClickFunction = () => this.props.dispatchShortlistAdd(IShortlistable.Title, source.title.trim());
        }

        return (
            <div id="eval_title" className="w_component_row">
                <b><a className={`badge ${badgeColor}`} onClick={onClickFunction}>{source.title}</a></b>
            </div>
        );
    }

    private yearToDiv(source: ISource) {
        if (this.state.editing) {
            return (
                <div id="eval_year" className="w_component_row">
                    <input className="form-control" type="number" id="edit_year" name="year"
                           required={true} value={source.year} onChange={this.handleYearChange}/>
                </div>
            );
        }

        let badgeColor;
        let onClickFunction;

        if (this.isShortlisted(IShortlistable.Year, source.year)) {
            badgeColor = "badge-success";
            onClickFunction = () => this.props.dispatchShortlistRemove(IShortlistable.Year, source.year);
        } else {
            badgeColor = "badge-info";
            onClickFunction = () => this.props.dispatchShortlistAdd(IShortlistable.Year, source.year);
        }

        return (
            <div id="eval_year" className="w_component_row">
                <b><a className={`badge ${badgeColor}`} onClick={onClickFunction}>{source.year}</a></b>
            </div>
        );
    }

    private authorsToDiv(source: ISource) {
        if (this.state.editing) {
            return (
                <input className="form-control" type="text" id="edit_authors" name="authors"
                       required={true}
                       value={source.authors.join(';')}
                       onChange={this.handleAuthorChange}/>
            );
        }

        return source.authors.map((author: string, authorId: number) => {
            let badgeColor;
            let onClickFunction;

            if (this.isShortlisted(IShortlistable.Author, author.trim())) {
                badgeColor = "badge-success";
                onClickFunction = () => this.props.dispatchShortlistRemove(IShortlistable.Author, author.trim());
            } else {
                badgeColor = "badge-info";
                onClickFunction = () => this.props.dispatchShortlistAdd(IShortlistable.Author, author.trim());
            }

            return (
                <a className={`badge ${badgeColor} mr-1`} key={authorId} onClick={onClickFunction}>
                    {author}
                </a>
            );
        });
    }

    private keywordsToDiv(source: ISource) {
        if (this.state.editing) {
            return (
                <input className="form-control" type="text" id="edit_keywords" name="keywords"
                       value={source.keywords.join(';')}
                       onChange={this.handleKeywordChange}/>
            );
        }

        return source.keywords.map((keyword: string, keywordId: number) => {
            let badgeColor;
            let onClickFunction;

            if (this.isShortlisted(IShortlistable.Keyword, keyword.trim())) {
                badgeColor = "badge-success";
                onClickFunction = () => this.props.dispatchShortlistRemove(IShortlistable.Keyword, keyword.trim());
            } else {
                badgeColor = "badge-info";
                onClickFunction = () => this.props.dispatchShortlistAdd(IShortlistable.Keyword, keyword.trim());
            }

            return (
                <a className={`badge ${badgeColor} mr-1`} key={keywordId} onClick={onClickFunction}>
                    {keyword}
                </a>
            );
        });
    }

    private abstractToDiv(source: ISource) {
        if (this.state.editing) {
            return (
                <textarea className="form-control" rows={10} id="edit_abstract" name="abstract"
                          value={source.abstract} onChange={this.handleAbstractChange}/>
            );
        }

        return (
            <b>{source.abstract}</b>
        );
    }

    private handleClassification(event: any) {
        if (!(this.state.source !== undefined && this.state.source._id !== undefined)) {
            return;
        }
        const source: ISource = {
            ...this.state.source,
            usability: parseInt(event.target.value, 10),
        };

        this.props.dispatchUpdateSource(source);
        this.props.history.push(`/evaluate`);
    }

    private sourceFromID(id: string): ISource {
        const source = this.props.sources.find((s: ISource) => s._id === id);

        if (!source) {
            throw new Error(`No source with ID: ${id}`);
        }

        return source;
    }

    private isShortlisted(type: IShortlistable, value: any): boolean {
        const index = this.props.shortlist
            .filter((element: IShortlistElement) => element.type === type)
            .map((element: IShortlistElement) => element.value)
            .indexOf(value);

        return index !== -1;
    }

    private handleQuote() {
        const selection = window.getSelection();

        if (!selection) {
            return;
        }

        this.props.dispatchShortlistAdd(IShortlistable.Quote, selection.toString().trim());
    }

    private modeSwitch() {
        if (this.state.source === undefined) {
            return;
        } else if (this.state.editing) {
            this.props.dispatchUpdateSource(this.state.source);
        }

        this.setState((state: IEvaluateState) => ({...state, editing: !state.editing}));
    }

    private handleTitleChange(event: any) {
        const source: ISource | undefined = this.state.source;
        if (source === undefined) {
            return;
        }

        source.title = event.target.value;
        this.setState((state: IEvaluateState) => ({...state, source}));
    }

    private handleYearChange(event: any) {
        const source: ISource | undefined = this.state.source;
        if (source === undefined) {
            return;
        }

        source.year = event.target.value;
        this.setState((state: IEvaluateState) => ({...state, source}));
    }

    private handleAuthorChange(event: any) {
        const source: ISource | undefined = this.state.source;
        if (source === undefined) {
            return;
        }

        source.authors = event.target.value.split(';');
        this.setState((state: IEvaluateState) => ({...state, source}));
    }

    private handleKeywordChange(event: any) {
        const source: ISource | undefined = this.state.source;
        if (source === undefined) {
            return;
        }

        source.keywords = event.target.value.split(';');
        this.setState((state: IEvaluateState) => ({...state, source}));
    }

    private handleAbstractChange(event: any) {
        const source: ISource | undefined = this.state.source;
        if (source === undefined) {
            return;
        }

        source.abstract = event.target.value;
        this.setState((state: IEvaluateState) => ({...state, source}));
    }

    private handleCommentChange(event: any) {
        clearTimeout(this.commentDispatcher);

        const source: ISource | undefined = this.state.source;
        if (source === undefined) {
            return;
        }

        source.comment = event.target.value;
        this.setState((state: IEvaluateState) => ({...state, source}));
        this.commentDispatcher = setTimeout(() => this.props.dispatchUpdateSource(source), 1000);
    }
}

export default EvaluateComponent;


// WEBPACK FOOTER //
// ./src/components/EvaluateComponent/EvaluateComponent.tsx